import { Button, Select, Space, Switch } from "antd";
import { ReactNode, useEffect, useState } from "react";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import {
  useAddOtherSettingMutation,
  useGetOtherSettingDataQuery,
} from "../../../features/leaveProrate/leaveProrateApi";
import useError from "../../../hooks/useError";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

const months = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

export default function EditInformationModal({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<string>) {
  const {
    data: otherSettingData,
    isLoading: isOtherSettingDataLoading,
    isError: isGetError,
    error: getError,
  } = useGetOtherSettingDataQuery();
  const [
    addOtherSetting,
    { isLoading: isUpdateLoading, isSuccess, isError, error },
  ] = useAddOtherSettingMutation();
  const [sandwichLeave, setSandwichLeave] = useState(
    otherSettingData?.data.others_info.sandwich_leave
  );
  const [leaveProrateEnable, setLeaveProrateEnable] = useState(
    otherSettingData?.data.others_info.is_leave_prorate_enable
  );
  useError(isGetError, getError, "Get others setting fail");
  useError(isError, error, "Others setting update fail");

  const currentMonth = months?.find(
    (month) => month?.label === data?.split(" - ")?.[0]
  );

  const [startingMonth, setStartingMonth] = useState<number | undefined>(
    currentMonth?.value ? currentMonth.value : undefined
  );

  const handleSave = () => {
    const bodyData = {
      sandwich_leave: Number(sandwichLeave) || 0,
      fiscal_year: startingMonth,
      is_leave_prorate_enable: Number(leaveProrateEnable) || 0,
    };

    addOtherSetting(bodyData);
  };

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Others setting updated successfully");
      return handleClose();
    }
  }, [handleClose, isSuccess]);

  return (
    <CustomModal
      title={"Update Other leave Information"}
      {...rest}
      handleClose={handleClose}
      width={800}
      footer={null}
    >
      {isOtherSettingDataLoading ? (
        <Loader />
      ) : (
        <>
          <table>
            <tbody>
              <Item
                type="Sandwich leave"
                description="A kind of leave policy in which a company deducts leaves of an
              employee for weekends and holidays if that employee applies leave
              the day after and before the weekends or holidays."
              >
                <div className="flex gap-2 items-center">
                  <Switch
                    checked={!!sandwichLeave}
                    onChange={(e) => setSandwichLeave(e ? 1 : 0)}
                  />
                  <span className="text-header text-sm font-medium">
                    {sandwichLeave ? "Activated" : "Deactivated"}
                  </span>
                </div>
              </Item>
              <Item
                type="Starting month of Fiscal year"
                description="One year period that companies and governments use for financial reporting and budgeting. Choose the starting month of your company fiscal year"
              >
                <div className="flex gap-2 items-center custom-form">
                  <Select
                    allowClear
                    showSearch
                    size="large"
                    value={startingMonth}
                    onChange={(e) => setStartingMonth(e)}
                    filterOption={true}
                    options={months}
                    className="max-w-80 w-full"
                    suffixIcon={<SelectDownIcon />}
                    rootClassName="icon-gray"
                  />
                </div>
              </Item>

              <Item
                type="Leave Prorate"
                description="Amount of leaves proportionally distributed in the entire fiscal year. If this feature is activated, employees leave types will be auto prorated based on their joining date ."
              >
                <div className="flex gap-2 items-center">
                  <Switch
                    checked={!!leaveProrateEnable}
                    onChange={(e) => setLeaveProrateEnable(e ? 1 : 0)}
                  />
                  <span className="text-header text-sm font-medium">
                    {leaveProrateEnable ? "Activated" : "Deactivated"}
                  </span>
                </div>
              </Item>
            </tbody>
          </table>

          <Space className="w-full mt-6" size={12}>
            <Button
              className="normal-dark-button modal-button"
              onClick={handleClose}
              size="middle"
            >
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              onClick={handleSave}
              loading={isUpdateLoading}
            >
              Save
            </Button>
          </Space>
        </>
      )}
    </CustomModal>
  );
}

interface ItemProps {
  type: string;
  description: string;
  children: ReactNode;
}

const Item = ({ type, description, children }: ItemProps) => (
  <tr>
    <td className="flex py-2 pr-6">
      <h6 className="text-header text-sm font-medium">{type}</h6>
    </td>
    <td className="py-2">
      {children}
      <div className="text-extra-gray text-sm font-medium mt-2">
        {description}
      </div>
    </td>
  </tr>
);
